import { Checkbox, Stack, Text, Title, TextInput } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";

type Props = {
  form: any;
};

const Component = ({ form }: Props) => {
  const { t } = useTranslation();

  // Actions state
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [noneSelected, setNoneSelected] = useState(false);
  const [showOtherInput, setShowOtherInput] = useState(false);

  const actions = [
    "Pienso que no sirve de nada hacerlo.",
    "No me pareció necesario hacerlo.",
    "Por vergüenza.",
    "No sabía o no existe un canal de denuncia para estos casos.",
    "No quería ser vista como una persona problemática.",
    "Tenía miedo de perder mi trabajo o de sufrir algún otro tipo de represalia.",
    "No sabía si era lo suficientemente grave para elevarlo.",
    "Otro (especificar).",
  ];

  const actionOptions = actions.map(
    (_, index) => `BAD_${String(index + 1).padStart(3, "0")}AL`
  );

  const noneOption = "BAD_NONE";
  const otherOption = "BAD_008AL"; // "Otro (especificar)" option

  // Update form values
  useEffect(() => {
    if (noneSelected) {
      // When "Ninguna de las anteriores" is selected, set N.A values
      form.setFieldValue("BAD_001_008AL", ["N.A"]);
      form.setFieldValue("BAD_008AL", ""); // Clear other input
      setShowOtherInput(false);
    } else {
      // Normal behavior when specific actions are selected
      form.setFieldValue("BAD_001_008AL", selectedValues);

      // Show/hide other input based on selection
      const otherSelected = selectedValues.includes(otherOption);
      setShowOtherInput(otherSelected);

      // Clear other input if not selected
      if (!otherSelected) {
        form.setFieldValue("BAD_008AL", "");
      }
    }
  }, [selectedValues, noneSelected]);

  const handleCheckboxChange = (value: string) => {
    if (value === noneOption) {
      if (noneSelected) {
        setNoneSelected(false);
        setSelectedValues([]);
      } else {
        setNoneSelected(true);
        setSelectedValues([noneOption]);
        setShowOtherInput(false);
        form.setFieldValue("BAD_008AL", ""); // Clear other input
      }
    } else {
      setNoneSelected(false);
      if (selectedValues.includes(value)) {
        setSelectedValues((prev) =>
          prev.filter((v) => v !== value && v !== noneOption)
        );

        // If unchecking "Otro", clear the text input
        if (value === otherOption) {
          form.setFieldValue("BAD_008AL", "");
          setShowOtherInput(false);
        }
      } else {
        setSelectedValues((prev) => [
          ...prev.filter((v) => v !== noneOption),
          value,
        ]);

        // If checking "Otro", show the text input
        if (value === otherOption) {
          setShowOtherInput(true);
        }
      }
    }
  };

  return (
    <Stack spacing="sm">
      <Text fw={500} size="sm" color="gray.8">
        ¿Por qué no elevaste la situación (con tu jefe/a, RRHH o el canal de
        denuncia)?
      </Text>
      <Text fz="xs" color="red">
        Puedes marcar más de una opción en esta pregunta.
      </Text>

      <Stack spacing={4}>
        {actions.map((action, index) => (
          <Stack key={actionOptions[index]} spacing={2}>
            <Checkbox
              label={action}
              checked={selectedValues.includes(actionOptions[index])}
              disabled={noneSelected}
              onChange={() => handleCheckboxChange(actionOptions[index])}
              size="sm"
              styles={{
                body: { alignItems: "center" },
                label: {
                  lineHeight: 1.2,
                  minHeight: "2rem",
                  display: "flex",
                  alignItems: "center",
                },
              }}
            />
            {/* Show TextInput only for "Otro (especificar)" option */}
            {actionOptions[index] === otherOption && showOtherInput && (
              <TextInput
                placeholder="Especificar..."
                {...form.getInputProps("BAD_008AL")}
                size="sm"
                ml="xl"
                width="400px"
                styles={{
                  input: {
                    fontSize: "14px",
                  },
                }}
              />
            )}
          </Stack>
        ))}
        <Checkbox
          label="Ninguna de las anteriores"
          checked={noneSelected}
          onChange={() => handleCheckboxChange(noneOption)}
          size="sm"
          color="gray"
          styles={{
            body: { alignItems: "center" },
            label: {
              lineHeight: 1.2,
              minHeight: "2rem",
              display: "flex",
              alignItems: "center",
            },
          }}
        />
      </Stack>
    </Stack>
  );
};

export default Component;
